//
// # usePersistentScroll
//
// Preserves a scroll container's scroll position across dockview tab
// switches.  When dockview switches tabs within a stack it reparents
// the panel's DOM, which resets the scroll container's `scrollTop` to
// 0.  This hook:
//
//   - Saves the last real `scrollTop` value on scroll events.
//   - Uses a ResizeObserver to detect when the element transitions
//     from zero-sized (detached / hidden) to nonzero-sized (reattached)
//     and restores the saved scroll position.
//
// The owning React component must stay mounted across tab switches
// (dockview does keep panels mounted even when it reparents their DOM).
//

import { useEffect, type RefObject } from "react";

export function usePersistentScroll(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let savedScrollTop = 0;

    const onScroll = () => {
      // Only record real user scrolls.  While the element is detached
      // the browser won't fire scroll events, and on reattach scrollTop
      // may briefly read 0 before we restore it — ignore that case.
      if (el.offsetHeight > 0 && el.scrollTop > 0) {
        savedScrollTop = el.scrollTop;
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    let wasHidden = el.offsetHeight === 0;
    const ro = new ResizeObserver(() => {
      const isHidden = el.offsetHeight === 0;
      if (wasHidden && !isHidden && savedScrollTop > 0) {
        el.scrollTop = savedScrollTop;
      }
      wasHidden = isHidden;
    });
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [ref]);
}
